// Slash commands: the few things worth saying without a sentence. Each is a
// prompt the agent gets as if you had typed it, a place to go, or an action the
// host does (`act`). The composer offers them (components/PrimeComposer.tsx):
// Prime's chats get CHAT_COMMANDS, a coding agent's thread AGENT_COMMANDS.
export interface ChatCommand {
  name: string
  hint: string
  /** what the agent is asked; omit for a command that only navigates or acts */
  prompt?: string
  /** where it takes you instead */
  to?: string
  /** something the host does: stop, end, preview, pr, prime */
  act?: string
}

export const CHAT_COMMANDS: ChatCommand[] = [
  { name: 'plan', hint: 'Plan the day from what is on', prompt: 'Plan my day.' },
  {
    name: 'today',
    hint: 'What is on today, and what to start',
    prompt: 'What is on today? What should I start with?',
  },
  {
    name: 'inbox',
    hint: 'Sort out what is waiting in the inbox',
    prompt: 'Go through my inbox and suggest where each thing should go.',
  },
  {
    name: 'stuck',
    hint: 'What has been sitting untouched',
    prompt: 'What have I been putting off? Be blunt.',
  },
  {
    name: 'week',
    hint: 'How the week ahead looks',
    prompt: 'How does the week ahead look? Anything bunched up?',
  },
  { name: 'recap', hint: 'How today went', prompt: 'How did today go?' },
  { name: 'new', hint: 'Start a fresh chat', to: '/agents' },
]

/** A coding agent's thread: asks of the agent, and the session's own controls. */
export const AGENT_COMMANDS: ChatCommand[] = [
  {
    name: 'status',
    hint: 'Where it is up to, in a line or two',
    prompt: 'Where are you up to? Two lines: what is done, what is left, and anything you need from me.',
  },
  {
    name: 'check',
    hint: 'Run the checks and say what failed',
    prompt:
      'Run .factory/check now and tell me the result. If something fails, say what and what you will do.',
  },
  {
    name: 'publish',
    hint: 'Commit, push and open or update the pull request',
    prompt:
      'Commit what you have, make sure .factory/check passes, and publish: open or update the pull request.',
  },
  { name: 'preview', hint: 'Try its branch: its app, running in its sandbox', act: 'preview' },
  { name: 'pr', hint: 'Open its pull request for review', act: 'pr' },
  { name: 'prime', hint: 'Ask Prime to look at what it did', act: 'prime' },
  { name: 'stop', hint: 'Stop what it is doing now', act: 'stop' },
  { name: 'end', hint: 'End this session', act: 'end' },
]

/** The commands of `list` matching what has been typed after a leading slash. */
export const matchCommands = (text: string, list: ChatCommand[] = CHAT_COMMANDS): ChatCommand[] => {
  if (!text.startsWith('/')) return []
  const q = text.slice(1).trim().toLowerCase()
  if (q.includes(' ')) return [] // past the command word: they are writing a sentence
  return list.filter((c) => c.name.startsWith(q))
}
